import { Injector } from '@angular/core';
import {
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
} from '@irembo-andela/irembogov3-common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IBasicFieldLabelAndDetails } from '../../../../models/basic-label-value-pair.model';
import {
  IFormbuilderFieldTypeConfiguration,
  IFormbuilderFieldTypeConfigurationData,
  TConfigurationFormModelTypes,
} from '../../../../models/form-builder-types-configurations.model';
import { IFormFieldsDisplayModel } from '../../../../models/form-fields-formly-display.model';
import {
  IAvailableValidations,
  IFileSizeValidationConfig,
  IFormFieldsValidationModel,
  IIdConditionsModel,
  TAvailableConditionalValidations,
  TAvailableValidations,
} from '../../../../models/form-fields-formly-validation.model';
import { TFormlyFieldKey } from '../../../../models/formly-key.type';
import { FormBuilderService } from '../../../../services/form-builder.service';
import { ValidationTypeDefaultMessage } from '../../form-builder-validation-type-message.utils';
import {
  FETCHED_DATA_FIELD_TYPES,
  getFieldDisplayFormConfig,
} from '../../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { parseFieldIntoDisplayFormModel } from '../../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import {
  genericLabelValueFieldParseIntoSettingsFormModel,
  genericLabelValueFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  generateValidationAndConditionsField,
  getConditionalUtilValidatorsFormConfig,
  getErrorMappingValidatorFormConfig,
} from '../../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { parseUtilValidatorsModelFromFieldIntoModel } from '../../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import { setUtilConditionalExpressionFromModel } from '../../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import {
  IFormlyFieldValidationFormsConfig,
  FORMLY_FIELDS_VALIDATION_FORMS_CONFIG,
} from '../../form-fields-formly-validations/form-fields-formly-validation-forms-parser-setter.config';
import { setErrorMappingValidation } from '../generic-datafetch-field-type.configuration';
import { SettersConfigurationUtils } from './utils/setters';

/**
 * newly implemented base class for defining the formly form, parser and setter for formly fields.
 */
export class BaseFormlyFieldConfiguration {
  protected formBuilderService!: FormBuilderService;

  protected settingsFormlyForm!: FormlyFieldConfig[];
  protected settingsFormlyModel: any = {};

  protected validationsFormlyForm!: FormlyFieldConfig[];
  protected validationsFormlyModel: any = {};

  protected displayFormlyForm!: FormlyFieldConfig[];
  protected displayFormlyModel: any = {};

  private isDataFetchField = false;
  protected availableValidations: TAvailableValidations[] = ['required'];
  protected availableConditionalValidations: TAvailableConditionalValidations[] =
    ['required'];

  constructor(private injector: Injector, protected field: FormlyFieldConfig) {
    this.formBuilderService = this.injector.get(FormBuilderService);
    this.isDataFetchField = FETCHED_DATA_FIELD_TYPES.includes(
      <EIremboFormlyFieldTypes>this.field.type
    );
  }

  protected getSettingsConfig(): IFormbuilderFieldTypeConfigurationData {
    return {
      formlyForm: this.settingsFormlyForm
        ? this.settingsFormlyForm
        : [...getDefaultBaseSettingsConfigFormly(this.formBuilderService)],
      formModel: this.settingsFormlyModel,
    };
  }

  protected getDisplayConfig():
    | IFormbuilderFieldTypeConfigurationData
    | undefined {
    return {
      formlyForm: this.displayFormlyForm
        ? this.displayFormlyForm
        : [...getFieldDisplayFormConfig(this.formBuilderService)],
      formModel: this.displayFormlyModel,
    };
  }

  protected getValidationsConfig():
    | IFormbuilderFieldTypeConfigurationData
    | undefined {
    return {
      formlyForm: this.buildValidationsFormlyFields(),
      formModel: this.validationsFormlyModel,
    };
  }

  protected buildValidationsFormlyFields(): FormlyFieldConfig[] {
    const validationsFormlyFields: FormlyFieldConfig[] = [];

    const availableValidations: FormlyFieldConfig | undefined =
      this.buildAvailableValidationsFormlyField();

    if (availableValidations) {
      validationsFormlyFields.push(availableValidations);
    }

    if (this.isDataFetchField) {
      validationsFormlyFields.push(
        getConditionalUtilValidatorsFormConfig(this.formBuilderService)
      );
    }

    if (this.field?.type === EIremboFormlyFieldTypes.customgenericdatafetch) {
      validationsFormlyFields.push(getErrorMappingValidatorFormConfig());
    }

    return validationsFormlyFields;
  }

  private buildAvailableValidationsFormlyField():
    | FormlyFieldConfig
    | undefined {
    const availableValidationsFieldGroup: FormlyFieldConfig[] = [];
    if (this.availableValidations.length < 1) {
      return;
    }
    this.availableValidations.forEach(
      (validationKey: keyof IAvailableValidations) => {
        const validationKeyConfig:
          | IFormlyFieldValidationFormsConfig
          | undefined = FORMLY_FIELDS_VALIDATION_FORMS_CONFIG[validationKey];
        const validationLogic: FormlyFieldConfig =
          validationKeyConfig?.formlyConfig(this.formBuilderService);

        const validationsFieldGroup: FormlyFieldConfig[] = [
          validationLogic,
          ...(this.availableConditionalValidations.includes(
            validationKey as TAvailableConditionalValidations
          )
            ? [generateValidationAndConditionsField(this.formBuilderService)]
            : []),
          this.getErrorMessageField(validationKey),
        ];

        const logicsField: FormlyFieldConfig = {
          key: 'logics',
          type: EIremboFormlyFieldTypes.customrepeater,
          defaultValue: [],
          props: {
            hideLabel: false,
            addText: 'Add Logic',
            maxItems:
              validationKeyConfig?.limitNumberOfValidations ||
              !this.availableConditionalValidations.includes(
                validationKey as TAvailableConditionalValidations
              )
                ? 1
                : undefined,
            template: 'displayRules',
            title: 'Logic',
          },
          fieldArray: {
            type: EIremboFormlyFieldTypes['formly-group'],
            fieldGroup: validationsFieldGroup,
          },
        };

        availableValidationsFieldGroup.push({
          key: validationKey,
          props: {
            label: validationKeyConfig?.label,
            collapsible: true,
            collapsed: true,
          },
          wrappers: ['service-validation-rule-wrapper'],
          fieldGroup: [logicsField],
        });
      }
    );

    return {
      key: 'validations',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        hideLabel: true,
      },
      fieldGroup: availableValidationsFieldGroup,
    };
  }

  private getErrorMessageField(validationKey: string): FormlyFieldConfig {
    switch (validationKey) {
      case 'idConditions':
        return this.getIdConditionsErrorMessagesField();
      case 'fileSize':
        return this.getFileSizeErrorMessagesField();
      case 'tableValidation':
        return this.getTableValidationErrorMessagesField();
      default:
        return {
          key: 'errorMessage',
          type: EIremboFormlyFieldTypes.input,
          defaultValue:
            FORMLY_FIELDS_VALIDATION_FORMS_CONFIG[
              <TAvailableValidations>validationKey
            ]?.defaultErrorMesage ?? '',
          props: {
            label: 'Error Message',
            placeholder: 'Enter error Message',
          },
        };
    }
  }

  private getIdConditionsErrorMessagesField(): FormlyFieldConfig {
    return {
      key: 'errorMessages',
      type: EIremboFormlyFieldTypes.block,
      props: {
        label: 'Error Messages',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          const idConditionsModel: IIdConditionsModel | undefined =
            field?.parent?.model?.['idConditions'];

          return !(
            idConditionsModel &&
            (idConditionsModel.ageLimit?.minimumAge ||
              idConditionsModel.ageLimit?.maximumAge)
          );
        },
      },
      fieldGroup: [
        {
          key: EIremboFormlyValidationTypes.minimumAge,
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Minimum Age validation error message',
            label: 'Minimum Age Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.minimumAge
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['idConditions']?.[
                'ageLimit'
              ]?.['minimumAge'];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['idConditions']?.[
                'ageLimit'
              ]?.['minimumAge'];
            },
          },
        },
        {
          key: EIremboFormlyValidationTypes.maximumAge,
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Maximum Age validation error message',
            label: 'Maximum Age Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.maximumAge
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['idConditions']?.[
                'ageLimit'
              ]?.['maximumAge'];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['idConditions']?.[
                'ageLimit'
              ]?.['maximumAge'];
            },
          },
        },
      ],
    };
  }

  private getFileSizeErrorMessagesField(): FormlyFieldConfig {
    return {
      key: 'errorMessages',
      type: EIremboFormlyFieldTypes.block,
      props: {
        label: 'Error Messages',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          const fileSize: IFileSizeValidationConfig | undefined =
            field?.parent?.model?.['fileSize'];

          return !(
            fileSize &&
            (fileSize.minimumUploadSize || fileSize.maximumUploadSize)
          );
        },
      },
      fieldGroup: [
        {
          key: 'minimumUploadSizeMessage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Minimum Upload Size validation error message',
            label: 'Minimum Upload Size Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.minimumUploadSize
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['fileSize']?.[
                'minimumUploadSize'
              ];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['fileSize']?.[
                'minimumUploadSize'
              ];
            },
          },
        },
        {
          key: 'maximumUploadSizeMessage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Maximum Upload Size validation error message',
            label: 'Maximum Upload Size Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.maximumUploadSize
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['fileSize']?.[
                'maximumUploadSize'
              ];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['fileSize']?.[
                'maximumUploadSize'
              ];
            },
          },
        },
      ],
    };
  }

  private getTableValidationErrorMessagesField(): FormlyFieldConfig {
    return {
      key: 'errorMessages',
      type: EIremboFormlyFieldTypes.block,
      props: {
        label: 'Error Messages',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          const tableValidation:
            | { minSelection?: number; maxSelection?: number }
            | undefined = field?.parent?.model?.['tableValidation'];

          return !(
            tableValidation &&
            (tableValidation.minSelection || tableValidation.maxSelection)
          );
        },
      },
      fieldGroup: [
        {
          key: 'minSelectionMessage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Minimum Selection validation error message',
            label: 'Minimum Selection Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.minSelection
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['tableValidation']?.[
                'minSelection'
              ];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['tableValidation']?.[
                'minSelection'
              ];
            },
          },
        },
        {
          key: 'maxSelectionMessage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            placeholder: 'Enter Maximum Selection validation error message',
            label: 'Maximum Selection Message:',
            required: false,
          },
          defaultValue: ValidationTypeDefaultMessage(
            EIremboFormlyValidationTypes.maxSelection
          ),
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.parent?.parent?.model?.['tableValidation']?.[
                'maxSelection'
              ];
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.parent?.model?.['tableValidation']?.[
                'maxSelection'
              ];
            },
          },
        },
      ],
    };
  }

  private getconfiguredFieldLabelAndDetails():
    | IBasicFieldLabelAndDetails<TFormlyFieldKey>
    | undefined {
    return this.formBuilderService.getCurrentConfiguredFieldLabelAndDetails();
  }

  getFormlyFieldConfigurationConfig(): IFormbuilderFieldTypeConfiguration {
    return {
      settings: this.getSettingsConfig(),
      validations: this.getValidationsConfig(),
      display: this.getDisplayConfig(),
    };
  }

  parseFieldConfigIntoSettingsFormModel(): TConfigurationFormModelTypes {
    return genericLabelValueFieldParseIntoSettingsFormModel(this.field);
  }

  updateFieldConfigFromSettingsFormModel(model: any): any {
    return genericLabelValueFieldUpdateFromSettingsFormModel(model, this.field);
  }

  parseFieldConfigIntoValidationsFormModel(): IFormFieldsValidationModel {
    let model: any = {};

    const configuredFieldLabelAndDetails:
      | IBasicFieldLabelAndDetails<TFormlyFieldKey>
      | undefined = this.getconfiguredFieldLabelAndDetails();
    model.configuredFieldRefPath = configuredFieldLabelAndDetails?.fieldRefPath;
    model.configuredFieldRootModelPath =
      configuredFieldLabelAndDetails?.rootModelPath;
    model.validations = this.field?.props?.['validationsModel'];
    model.errorMapping = this.field?.props?.['errorMapping'];

    if (this.isDataFetchField) {
      model = parseUtilValidatorsModelFromFieldIntoModel(model, this.field);
    }

    return model;
  }

  updateFieldConfigFromValidationsFormModel = (
    model: IFormFieldsValidationModel
  ): FormlyFieldConfig => {
    this.availableValidations.forEach(validationKey => {
      const validationKeyConfig =
        FORMLY_FIELDS_VALIDATION_FORMS_CONFIG[validationKey];

      this.field = validationKeyConfig?.setter(model, this.field);
    });
    this.availableConditionalValidations.forEach(validationKey => {
      this.field = SettersConfigurationUtils.setConditionalExpressionFromModel(
        model,
        this.field,
        validationKey
      );
    });

    if (this.isDataFetchField) {
      this.field = setUtilConditionalExpressionFromModel(model, this.field);
    }

    if (this.field.type === EIremboFormlyFieldTypes.customgenericdatafetch) {
      this.field = setErrorMappingValidation(model, this.field);
    }

    return this.field;
  };

  parseFieldConfigIntoDisplayFormModel(): IFormFieldsDisplayModel {
    return parseFieldIntoDisplayFormModel(this.field);
  }

  updateFieldConfigFromDisplayFormModel(
    model: IFormFieldsDisplayModel
  ): FormlyFieldConfig {
    return setDisplayHideExpressionFromModel(model, this.field);
  }
}
